import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DataTable } from '@/components/shared/DataTable';
import type {
  ForecastReviewDataValidationProductRow,
  ForecastReviewDataValidationProductsResponse,
} from '@/services/forecastReviewService';

export type DataValidationSelectedProduct = {
  product_id: number;
  warehouse_id: number;
  product_name: string | null;
  sku: string | null;
};

function productKey(productId: number, warehouseId: number): string {
  return `${productId}:${warehouseId}`;
}

function periodUnitLabel(unit: 'week' | 'month' | 'day', t: (key: string) => string): string {
  if (unit === 'month') return t('forecastModule.dataValidation.periodUnit.month');
  if (unit === 'day') return t('forecastModule.dataValidation.periodUnit.day');
  return t('forecastModule.dataValidation.periodUnit.week');
}

interface DataValidationProductsTableProps {
  data: ForecastReviewDataValidationProductsResponse | null;
  loading: boolean;
  periodUnit: 'week' | 'month' | 'day';
  selected: DataValidationSelectedProduct | null;
  onSelect: (row: DataValidationSelectedProduct | null) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
}

export function DataValidationProductsTable({
  data,
  loading,
  periodUnit,
  selected,
  onSelect,
  onPageChange,
  onPageSizeChange,
}: DataValidationProductsTableProps) {
  const { t } = useTranslation();
  const periodUnitText = periodUnitLabel(periodUnit, t);

  const columns = useMemo(
    () => [
      {
        header: t('forecastModule.dataValidation.columns.product'),
        accessor: 'product_name',
        cell: (_: string, item: ForecastReviewDataValidationProductRow) => (
          <div>
            <div className="font-medium text-gray-900">{item.product_name ?? '—'}</div>
            {item.sku ? <div className="text-xs text-gray-500">{item.sku}</div> : null}
          </div>
        ),
      },
      {
        header: t('forecastModule.dataValidation.columns.stockouts'),
        accessor: 'stockout_count',
        align: 'right' as const,
        cell: (value: number) => <span className="text-gray-900">{value}</span>,
      },
      {
        header: t('forecastModule.dataValidation.columns.demandOutliers'),
        accessor: 'demand_outlier_count',
        align: 'right' as const,
        cell: (value: number) => <span className="text-gray-900">{value}</span>,
      },
      {
        header: t('forecastModule.dataValidation.columns.gapCount', { unit: periodUnitText }),
        accessor: 'gap_count',
        align: 'right' as const,
        cell: (value: number) => <span className="font-medium text-gray-900">{value}</span>,
      },
    ],
    [t, periodUnitText]
  );

  const handleRowClick = (row: ForecastReviewDataValidationProductRow) => {
    const key = productKey(row.product_id, row.warehouse_id);
    const selectedKey = selected ? productKey(selected.product_id, selected.warehouse_id) : null;
    if (key === selectedKey) {
      onSelect(null);
      return;
    }
    onSelect({
      product_id: row.product_id,
      warehouse_id: row.warehouse_id,
      product_name: row.product_name,
      sku: row.sku,
    });
  };

  if (loading && !data?.results.length) {
    return (
      <div className="py-8 text-center text-sm text-gray-500">
        {t('forecastModule.dataValidation.loadingProducts')}
      </div>
    );
  }

  if (!data?.results.length) {
    return (
      <div className="py-8 text-center text-sm text-gray-500">
        {t('forecastModule.dataValidation.emptyProducts')}
      </div>
    );
  }

  return (
    <DataTable
      columns={columns}
      data={data.results}
      compact
      loading={loading}
      pagination
      onRowClick={handleRowClick}
      rowClassName={(item: ForecastReviewDataValidationProductRow) => {
        const isSelected =
          selected?.product_id === item.product_id && selected?.warehouse_id === item.warehouse_id;
        return isSelected ? 'bg-blue-50' : '';
      }}
      externalPagination={{
        currentPage: data.page,
        totalPages: data.total_pages,
        totalCount: data.count,
        pageSize: data.page_size,
        onPageChange,
        onPageSizeChange,
      }}
    />
  );
}
